import { logger } from "@octopusdeploy/logging";

export async function isChameleonCampaignActive(campaignName: string, timeoutInMs: number): Promise<boolean> {
    try {
        if (!window.chmln) {
            return false;
        }

        // We timeout if it takes too long for chameleon to load. We don't want the UI to wait a long time for the campaigns to load,
        // when the rest of the UI might already be ready to show the user.
        // In most cases, this will immediately resolve because the data is already available.
        // The exception is for initial page loads, where chameleon may not have yet loaded the initial set of data it needs.
        await withTimeout(requiredChameleonDataHasLoaded(window.chmln), timeoutInMs);
        const campaigns = window.chmln.data?.campaigns ?? [];
        const campaign = campaigns.find((c) => c.get("name") === campaignName);
        if (campaign === undefined) {
            return false;
        }
        return campaign.isAvailable();
    } catch (error) {
        // The internal chameleon APIs may change over time. If this happens, we shouldn't blow up, but instead should gracefully assume the campaign is inactive.
        // We also fall through to this branch if there was a timeout waiting for chameleon to load
        logger.warn(error, "Unable to determine whether chameleon {campaignName} was active", { campaignName });
        return false;
    }
}

function withTimeout<T>(promise: Promise<T>, timeoutInMs: number): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => {
            setTimeout(() => reject(new Error("Timeout waiting for chameleon to load")), timeoutInMs);
        }),
    ]);
}

async function requiredChameleonDataHasLoaded(chameleon: NonNullable<typeof window.chmln>): Promise<void> {
    // There are two phases that chameleon goes through when loading information about the current user
    // 1. Loading the account. This identifies the chameleon account (i.e. Octopus Deploy). We have a list of campaigns at this point, but we don't know which are active for the current user.
    // 2. Loading the profile. This identifies the current user. We now know which segments the user is in, and can identify which campaigns are relevant for the current user
    // So before working out which campaigns are active, we need to make sure the account and the profile have loaded
    // While it always seems that the profile is loaded after the account, we are checking for both here independently to be safe,
    // in case this assumption is false or this behavior ever changes
    const accountIsAvailable = chameleonPropertyIsAvailable(chameleon, "account");
    const profileIsAvailable = chameleonPropertyIsAvailable(chameleon, "profile");

    await accountIsAvailable;
    await profileIsAvailable;
}

function chameleonPropertyIsAvailable(chameleon: NonNullable<typeof window.chmln>, dataType: "account" | "profile"): Promise<void> {
    return new Promise((resolve) => {
        // If chameleon has already loaded the data type (profile or account),
        // then the supplied callback will be immediately invoked.
        // This makes it safe to repeatedly call this function many times through the lifecycle of the app
        chameleon.on(`after:${dataType}`, () => {
            resolve();
        });
    });
}
